import React from "react";
import { HiArrowNarrowRight } from "react-icons/hi";
import { Link } from "react-scroll";
import { motion } from "framer-motion";
import ProfileImg from "../Pngs/Alok Singh.png";

interface HomeProps {
  onOpenResume: () => void;
}

export const Home: React.FC<HomeProps> = ({ onOpenResume }) => {
  // Stagger container animation
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants: any = {
    hidden: { y: 30, opacity: 0, filter: "blur(5px)" },
    visible: {
      y: 0,
      opacity: 1,
      filter: "blur(0px)",
      transition: { duration: 0.6, ease: "easeOut" },
    },
  };

  return (
    <div
      id="home"
      className="w-full min-h-screen bg-cyber-black flex items-center justify-center pt-24 pb-12 px-6 md:px-12 relative overflow-hidden"
    >
      <div className="max-w-6xl w-full grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-center relative z-10">
        
        {/* Info Left */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="md:col-span-7 flex flex-col justify-center text-left order-2 md:order-1"
        >
          <motion.p
            variants={itemVariants}
            className="text-cyber-cyan font-mono text-xs tracking-[0.3em] uppercase mb-3"
          >
            // CORE_MODULE_INITIALIZED
          </motion.p>
          
          <motion.span variants={itemVariants} className="text-cyber-gray-300 font-mono text-sm mb-1">
            Hi, my name is
          </motion.span>
          
          <motion.h1
            variants={itemVariants}
            className="text-4xl sm:text-7xl font-bold tracking-tight text-white font-sans"
          >
            Alok{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyber-cyan to-cyber-purple drop-shadow-[0_0_15px_rgba(0,240,255,0.2)]">
              Singh
            </span>
          </motion.h1>
          
          <motion.h2
            variants={itemVariants}
            className="text-xl sm:text-3xl font-semibold text-cyber-purple font-mono mt-3"
          >
            &lt;Full Stack Developer /&gt;
          </motion.h2>

          <motion.p
            variants={itemVariants}
            className="text-cyber-gray-300 py-6 max-w-[600px] text-sm md:text-base leading-relaxed"
          >
            A Full Stack Developer originally from Orai (UP), based in Ghaziabad/Noida. Driven by engineering high-performance backend architectures and pixel-perfect UI systems. Balancing enterprise-level development with academic pursuits in MCA. Focused on delivering elegant, scalable, and responsive digital products.
          </motion.p>

          <motion.div variants={itemVariants} className="flex flex-wrap gap-4 mt-2">
            <Link to="project" smooth={true} duration={500} className="cursor-pointer">
              <motion.button
                whileHover={{ scale: 1.05, boxShadow: "0 0 20px rgba(0, 240, 255, 0.4)" }}
                whileTap={{ scale: 0.98 }}
                className="group px-6 py-3 border border-cyber-cyan text-cyber-cyan hover:bg-cyber-cyan hover:text-black font-mono text-sm tracking-wider font-bold transition-all duration-300 rounded shadow-glow-cyan-sm flex items-center gap-2"
              >
                VIEW ARCHIVE
                <HiArrowNarrowRight className="group-hover:translate-x-1.5 duration-200" />
              </motion.button>
            </Link>

            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              id="resume-button-2"
              onClick={onOpenResume}
              className="px-6 py-3 bg-cyber-darker border border-cyber-gray-600 text-cyber-gray-300 hover:text-white hover:border-cyber-purple font-mono text-sm tracking-wider transition-all duration-300 rounded cursor-pointer text-center"
            >
              VIEW RESUME
            </motion.button>
          </motion.div>
        </motion.div>

        {/* Profile Right */}
        <div className="md:col-span-5 flex justify-center order-1 md:order-2">
          <motion.div
            initial={{ opacity: 0, scale: 0.9, filter: "blur(10px)" }}
            animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="relative group w-64 h-64 sm:w-80 sm:h-80"
          >
            {/* Outer Glowing Background Aura */}
            <div className="absolute inset-0 bg-gradient-to-tr from-cyber-cyan via-transparent to-cyber-purple rounded-[30%_70%_70%_30%_/_30%_30%_70%_70%] animate-pulse-slow blur-[12px] opacity-60 group-hover:opacity-85 group-hover:scale-105 transition-all duration-500" />
            
            {/* The Clip Path Container */}
            <div className="w-full h-full bg-cyber-darker p-[2px] rounded-[30%_70%_70%_30%_/_30%_30%_70%_70%] border border-cyber-cyan/30 overflow-hidden relative group-hover:border-cyber-cyan transition duration-500">
              <div className="w-full h-full bg-cyber-black rounded-[30%_70%_70%_30%_/_30%_30%_70%_70%] overflow-hidden relative">
                <img 
                  src={ProfileImg} 
                  alt="Alok Singh" 
                  className="w-full h-full object-cover filter grayscale hover:grayscale-0 contrast-[1.05] transition-all duration-700 ease-in-out scale-105 group-hover:scale-110"
                />
                
                {/* Screen Scanlines / Overlay */}
                <div className="absolute inset-0 bg-[linear-gradient(to_bottom,rgba(0,0,0,0)_95%,rgba(0,240,255,0.08)_95%)] bg-[size:100%_12px] pointer-events-none opacity-40" />
                <div className="absolute inset-0 bg-gradient-to-t from-cyber-black/50 via-transparent to-transparent opacity-60" />
              </div>
            </div>

            {/* Futuristic Tech Borders */}
            <div className="absolute -top-3 -left-3 w-8 h-8 border-t-2 border-l-2 border-cyber-cyan pointer-events-none opacity-80" />
            <div className="absolute -bottom-3 -right-3 w-8 h-8 border-b-2 border-r-2 border-cyber-purple pointer-events-none opacity-80" />
          </motion.div>
        </div>

      </div>
    </div>
  );
};
